import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { DateTimeInput } from '../ui/date-time-input';
import { refuseIfDateInputIncomplete } from '../../lib/dateInputs';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import ConfirmDialog from '../ui/ConfirmDialog';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { saisieDateIncomplete } from '../../hooks/useAutoSave';
import { useFormAutoSave } from '../../hooks/useFormAutoSave';

// Un code promo dans un panneau latéral (2026-09-27). Les dates sont saisies
// en heure locale et voyagent en UTC ISO ; une date à moitié tapée est
// refusée (`refuseIfDateInputIncomplete` à la création, `saisieDateIncomplete`
// au blur, 2026-09-09).
//
// Sauvegarde automatique (règle du 22/09) : texte et dates au blur, cases et
// interrupteurs au clic. Le type et la valeur partent TOUJOURS ensemble : la
// valeur n'a de sens que par le type (pourcentage ou centimes), et les deux
// bornes de validité aussi (elles se valident l'une par l'autre).

export interface EventPromoCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed_amount';
  value: number;
  applicable_category_ids: string[] | null;
  max_uses: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  auto_apply: boolean;
  is_active: boolean;
  created_at: string;
}

export interface PromoCodeFormValues {
  code: string;
  type: 'percentage' | 'fixed_amount';
  /** Pourcentage, ou euros (converti en centimes à l'envoi). */
  value: string;
  applicable_category_ids: string[];
  max_uses: string;
  valid_from: string;
  valid_until: string;
  auto_apply: boolean;
  is_active: boolean;
}

export interface PromoCodePatch {
  code?: string;
  type?: 'percentage' | 'fixed_amount';
  value?: number;
  applicable_category_ids?: string[] | null;
  max_uses?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  auto_apply?: boolean;
  is_active?: boolean;
}

/** UTC (base) → `YYYY-MM-DDTHH:mm` local pour `datetime-local`. */
export const toLocalDatetimeInput = (utc: string | null): string => {
  if (!utc) return '';
  const d = new Date(utc);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const toUTC = (local: string): string | null => (local ? new Date(local).toISOString() : null);

function toValues(c: EventPromoCode | null): PromoCodeFormValues {
  return {
    code: c?.code ?? '',
    type: c?.type ?? 'percentage',
    value: c ? (c.type === 'percentage' ? String(c.value) : (c.value / 100).toFixed(2)) : '10',
    applicable_category_ids: c?.applicable_category_ids ?? [],
    max_uses: c?.max_uses != null ? String(c.max_uses) : '',
    valid_from: toLocalDatetimeInput(c?.valid_from ?? null),
    valid_until: toLocalDatetimeInput(c?.valid_until ?? null),
    auto_apply: c?.auto_apply ?? false,
    is_active: c?.is_active ?? true,
  };
}

/** Le payload complet d'un code, tel que le serveur le lit. */
export const promoPayload = (v: PromoCodeFormValues) => ({
  code: v.code.trim().toUpperCase(),
  type: v.type,
  value: v.type === 'percentage' ? Number(v.value) : Math.round(Number(v.value) * 100),
  applicable_category_ids: v.applicable_category_ids.length ? v.applicable_category_ids : null,
  max_uses: v.max_uses ? parseInt(v.max_uses, 10) : null,
  valid_from: toUTC(v.valid_from),
  valid_until: toUTC(v.valid_until),
  auto_apply: v.auto_apply,
  is_active: v.is_active,
});

const toPatch = (fields: (keyof PromoCodeFormValues)[], v: PromoCodeFormValues): PromoCodePatch => {
  const full = promoPayload(v);
  const p: PromoCodePatch = {};
  if (fields.includes('code')) p.code = full.code;
  if (fields.includes('type') || fields.includes('value')) { p.type = full.type; p.value = full.value; }
  if (fields.includes('applicable_category_ids')) p.applicable_category_ids = full.applicable_category_ids;
  if (fields.includes('max_uses')) p.max_uses = full.max_uses;
  if (fields.includes('valid_from') || fields.includes('valid_until')) { p.valid_from = full.valid_from; p.valid_until = full.valid_until; }
  if (fields.includes('auto_apply')) p.auto_apply = full.auto_apply;
  if (fields.includes('is_active')) p.is_active = full.is_active;
  return p;
};

interface Props {
  open: boolean;
  promo: EventPromoCode | null;
  categories: { id: string; name: string }[];
  saving: boolean;
  serverError: string;
  /** Création seulement. */
  onSubmit: (values: PromoCodeFormValues) => void;
  /** Édition : écriture partielle, immédiate. Lève sur refus. */
  onPatch: (patch: PromoCodePatch) => Promise<void>;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function PromoCodeSheet({ open, promo, categories, saving, serverError, onSubmit, onPatch, onClose, onDirtyChange, onPrev, onNext }: Props) {
  const { t } = useTranslation();
  const editing = !!promo;

  const schema = useMemo(() => z.object({
    code: z.string().trim().min(1, t('promoCodes.codeRequired')),
    type: z.enum(['percentage', 'fixed_amount']),
    value: z.string().refine((v) => Number(v) > 0, t('promoCodes.sheet.valueRequired')),
    applicable_category_ids: z.array(z.string()),
    max_uses: z.string(),
    valid_from: z.string(),
    valid_until: z.string(),
    auto_apply: z.boolean(),
    is_active: z.boolean(),
  }).superRefine((v, ctx) => {
    if (v.type === 'percentage' && Number(v.value) > 100) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: t('promoCodes.sheet.percentMax') });
    if (v.valid_from && v.valid_until && v.valid_from > v.valid_until) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['valid_until'], message: t('promoCodes.sheet.datesInverted') });
  }), [t]);

  const form = useForm<PromoCodeFormValues>({ resolver: zodResolver(schema), defaultValues: toValues(promo), mode: 'onTouched' });
  const autoSave = useFormAutoSave<PromoCodeFormValues, PromoCodePatch>(form, {
    enabled: editing,
    save: onPatch,
    toPatch,
    onError: (err: any) => toast.error(err?.response?.data?.detail || t('promoCodes.saveError')),
  });
  const { saveFields } = autoSave;

  useEffect(() => {
    autoSave.reset(toValues(promo));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.id, open]);

  const isDirty = !editing && form.formState.isDirty;
  useEffect(() => { onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);
  const [confirmDiscard, setConfirmDiscard] = useState<null | (() => void)>(null);
  const guard = (action: () => void) => (isDirty ? setConfirmDiscard(() => action) : action());
  const type = form.watch('type');
  // Une date à moitié tapée : React la voit vide, le navigateur sait qu'elle est
  // incomplète. On n'écrit pas `null` par-dessus une vraie date.
  const blurDate = (e: React.FocusEvent<HTMLInputElement>) => { if (!saisieDateIncomplete(e)) void saveFields(['valid_from', 'valid_until']); };

  return (
    <>
      <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) guard(onClose); }}>
        <SheetContent side="right" hideClose onInteractOutside={(e) => e.preventDefault()} className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[520px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit((v) => { if (!refuseIfDateInputIncomplete(t)) onSubmit(v); })} className="flex h-full min-h-0 flex-col" noValidate>
              <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SheetDescription className="text-xs">
                    {promo ? t('promoCodes.sheet.usedCount', { used: promo.used_count, max: promo.max_uses ?? '∞' }) : t('promoCodes.sheet.createHint')}
                  </SheetDescription>
                  <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">{promo ? promo.code : t('promoCodes.create')}</SheetTitle>
                </div>
                {promo && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={() => onPrev && guard(onPrev)}><ChevronUp size={16} /></Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={() => onNext && guard(onNext)}><ChevronDown size={16} /></Button>
                  </>
                )}
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => guard(onClose)}><X size={16} /></Button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
                <FormField control={form.control} name="code" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('promoCodes.code')}</FormLabel>
                    <FormControl><Input {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} onBlur={() => { field.onBlur(); void saveFields('code'); }} placeholder="EARLYBIRD" className="font-mono uppercase" autoFocus={!promo} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="type" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('promoCodes.type')}</FormLabel>
                      <Select value={field.value} onValueChange={(v) => { if (v) { field.onChange(v); void saveFields(['type', 'value']); } }}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="percentage">{t('promoCodes.percentage')}</SelectItem>
                          <SelectItem value="fixed_amount">{t('promoCodes.fixedAmount')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="value" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{type === 'percentage' ? t('promoCodes.sheet.valuePercent') : t('promoCodes.sheet.valueEuros')}</FormLabel>
                      <FormControl><Input type="number" min={0} step={type === 'percentage' ? 1 : 0.01} {...field} onBlur={() => { field.onBlur(); void saveFields(['type', 'value']); }} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="applicable_category_ids" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('promoCodes.categories')}</FormLabel>
                    <div className="flex flex-col gap-2">
                      {categories.map((c) => (
                        <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                          <Checkbox checked={field.value.includes(c.id)} onCheckedChange={(v) => { field.onChange(v === true ? [...field.value, c.id] : field.value.filter((id) => id !== c.id)); void saveFields('applicable_category_ids'); }} />
                          {c.name}
                        </label>
                      ))}
                      {categories.length === 0 && <span className="text-sm text-muted-foreground">{t('promoCodes.noCategories')}</span>}
                    </div>
                    <FormDescription>{t('promoCodes.categoriesHint')}</FormDescription>
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="valid_from" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('promoCodes.validFrom')}</FormLabel>
                      <FormControl><DateTimeInput {...field} onBlur={(e) => { field.onBlur(); blurDate(e); }} /></FormControl>
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="valid_until" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('promoCodes.validUntil')}</FormLabel>
                      <FormControl><DateTimeInput {...field} onBlur={(e) => { field.onBlur(); blurDate(e); }} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="max_uses" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('promoCodes.maxUses')}</FormLabel>
                    <FormControl><Input type="number" min={1} {...field} onBlur={() => { field.onBlur(); void saveFields('max_uses'); }} placeholder={t('promoCodes.unlimited')} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="auto_apply" render={({ field }) => (
                  <FormItem className="flex items-center gap-3 space-y-0">
                    <FormControl><Switch checked={field.value} onCheckedChange={(v) => { field.onChange(v); void saveFields('auto_apply'); }} /></FormControl>
                    <div><FormLabel>{t('promoCodes.autoApply')}</FormLabel><FormDescription>{t('promoCodes.autoApplyHint')}</FormDescription></div>
                  </FormItem>
                )} />
                <FormField control={form.control} name="is_active" render={({ field }) => (
                  <FormItem className="flex items-center gap-3 space-y-0">
                    <FormControl><Switch checked={field.value} onCheckedChange={(v) => { field.onChange(v); void saveFields('is_active'); }} /></FormControl>
                    <div><FormLabel>{t('promoCodes.active')}</FormLabel><FormDescription>{t('promoCodes.sheet.activeHint')}</FormDescription></div>
                  </FormItem>
                )} />
                {serverError && <Alert variant="destructive"><AlertDescription>{serverError}</AlertDescription></Alert>}
              </div>

              <div className="flex items-center gap-2 border-t border-white/[0.08] px-6 py-4">
                <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                  {editing && <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />}
                </span>
                {promo ? (
                  <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
                ) : (
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="animate-spin" />}
                    {t('common.create')}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard !== null}
        onOpenChange={(o) => { if (!o) setConfirmDiscard(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { const next = confirmDiscard; setConfirmDiscard(null); form.reset(toValues(promo)); next?.(); }}
      />
    </>
  );
}
